/**
 * <s-sort-menu> — custom sort dropdown that always opens BELOW its trigger (merchant request: the native macOS select
 * menu opens on top of the field). The real <select name="sort_by"> stays inside the element (visually hidden) so
 * the form, no-JS submit and s-live-form keep working: choosing an option sets select.value and dispatches a
 * bubbling `change` event, exactly like a native pick.
 *
 * Markup contract:
 *   <s-sort-menu>
 *     <select name="sort_by" data-sort-native>…</select>
 *     <button type="button" data-sort-trigger aria-haspopup="listbox" aria-expanded="false"><span data-sort-label></span>…</button>
 *     <ul role="listbox" data-sort-list hidden><li role="option" data-value="…" aria-selected="…">…</li></ul>
 *   </s-sort-menu>
 */
class SSortMenu extends HTMLElement {
  #select;
  #trigger;
  #list;
  #label;
  #onDocPointer = (e) => {
    if (!this.contains(/** @type {Node} */ (e.target))) this.close();
  };

  connectedCallback() {
    this.#select = this.querySelector('[data-sort-native]');
    this.#trigger = this.querySelector('[data-sort-trigger]');
    this.#list = this.querySelector('[data-sort-list]');
    this.#label = this.querySelector('[data-sort-label]');
    if (!this.#select || !this.#trigger || !this.#list) return;

    this.dataset.enhanced = '';
    this.#trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    this.#trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.open();
      }
    });
    this.#list.addEventListener('click', (e) => {
      const option = /** @type {HTMLElement} */ (e.target).closest('[role="option"]');
      if (option) this.#choose(option);
    });
    this.#list.addEventListener('keydown', (e) => this.#onListKey(e));
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onDocPointer);
  }

  get isOpen() {
    return this.#trigger?.getAttribute('aria-expanded') === 'true';
  }

  open() {
    if (this.isOpen) return;
    this.#list.hidden = false;
    this.#trigger.setAttribute('aria-expanded', 'true');
    const current = this.#list.querySelector('[aria-selected="true"]') || this.#list.querySelector('[role="option"]');
    /** @type {HTMLElement} */ (current)?.focus();
    document.addEventListener('pointerdown', this.#onDocPointer);
  }

  close(focusTrigger = false) {
    if (!this.isOpen) return;
    this.#list.hidden = true;
    this.#trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.#onDocPointer);
    if (focusTrigger) this.#trigger.focus();
    else this.#trigger.blur();
  }

  /** @param {HTMLElement} option */
  #choose(option) {
    const value = option.dataset.value ?? '';
    this.#list.querySelectorAll('[role="option"]').forEach((el) => el.setAttribute('aria-selected', String(el === option)));
    if (this.#label) this.#label.textContent = option.textContent?.trim() ?? '';
    this.close();
    if (this.#select.value !== value) {
      this.#select.value = value;
      this.#select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {KeyboardEvent} e */
  #onListKey(e) {
    const options = /** @type {HTMLElement[]} */ ([...this.#list.querySelectorAll('[role="option"]')]);
    const index = options.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      options[Math.min(index + 1, options.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      options[Math.max(index - 1, 0)]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (options[index]) this.#choose(options[index]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.close(true);
    } else if (e.key === 'Tab') {
      this.close();
    }
  }
}

if (!customElements.get('s-sort-menu')) customElements.define('s-sort-menu', SSortMenu);
